<template>
	<div class="field-definition">
		<div class="header flex-space-between--center mb-1">
			<div class="field-name">{{ fieldTitleText }}</div>
			<div class="flex-centerize">
				<div class="field-required flex-centerize mr-4">
					<div class="mr-2">Required</div>
					<AppControlledSwitch :input-value="fieldDefinition['is_required']" @change="onChangeRequired" class="swtch sml" :key="count" />
				</div>
				<div class="">
					<AppDropdownMenu :items="localMenuItems" content-class="men" min-width="200" absolute left bottom>
						<template #activator>
							<v-btn tabindex="-1" class="flex-centerize" width="24" height="26" icon fab>
								<SIcon class="menu-button" name="menu-button" width="20" />
							</v-btn>
						</template>
						<template #list-item="{ item }">
							<div class="menu-item py-1">
								{{ item.text }}
							</div>
						</template>
					</AppDropdownMenu>
				</div>
			</div>
		</div>

		<AppTextField outlined hide-details :value="fieldDefinition.name" :error-messages="nameError" @blur="onBlur" />

		<div class="">
			<AppSelect
				:items="selectItems"
				:value="fieldDefinition.type"
				class="more-fields"
				outlined
				item-value="type"
				item-text="text"
				:error-messages="typeError"
				:require="isShowErrors"
				@change="onTypeSelected"
			>
				<template v-slot:prepend-inner>
					<SIcon class="inner-prepended-data-type" :name="`${selectItems.icon}`" />
				</template>
				<template v-slot:item="{ item }">
					<div class="flex-start--center">
						<SIcon class="inner-prepended-data-type mt-1 mr-3" :name="`${item.icon}`" />
						<div>{{ item.text }}</div>
					</div>
				</template>
			</AppSelect>
		</div>
	</div>
</template>

<script>
import AppDialogManagerService from '@/components/dialogs/AppDialogManager/app-dialog-manager-service';

import { DATA_TYPES } from '../data-types.const';
import { APP_CONFIRMATION_DIALOG_RESPONSES } from '@/components/dialogs/AppDialogs/app-confirmation-dialog.const.js';

export default {
	name: 'FieldDefinition',
	components: {},
	props: {
		fieldDefinition: { type: Object },
		fieldTitleText: { type: String },
		fieldTypeText: { type: String },
		isShowErrors: { type: Boolean },
		subflowsUsageWorkflowIds: { type: Array },
	},
	data() {
		return {
			localMenuItems: [
				{ text: 'Delete', onClick: () => this.onDelete() },
				{ text: 'Duplicate', onClick: () => this.$emit('duplicateFieldDefinition') },
			],
			selectItems: [...DATA_TYPES],
			count: 0, // in order to force Switch to re-render so it will be a Controlled Input
		};
	},
	computed: {
		nameError() {
			return this.isShowErrors && this.fieldDefinition?.name.length === 0 ? 'Field name is required' : '';
		},
		typeError() {
			return this.isShowErrors && this.fieldDefinition?.type === null ? 'Type is required' : '';
		},
		amountSubflowUsage() {
			return this.subflowsUsageWorkflowIds.length;
		},
	},
	methods: {
		async onChangeRequired(value) {
			this.count += 1;
			const nextV = (await this.requiredGuardFunc()) ? value : !value;
			const newV = { ...this.fieldDefinition, ['is_required']: nextV };
			this.$emit('onFieldDefinition', newV);
		},
		async onDelete() {
			if (await this.deleteGuardFunc()) {
				this.$emit('deleteFieldDefinition');
			}
		},
		onBlur(e) {
			this.$emit('onFieldDefinition', { ...this.fieldDefinition, ...{ name: e.target.value } });
		},
		onTypeSelected(v) {
			this.$emit('onFieldDefinition', { ...this.fieldDefinition, ...{ type: v } });
		},
		async requiredGuardFunc() {
			const dialogProps = {
				title: 'Are you sure you want to make this input field required?',
				description: `This subflow is used by ${this.amountSubflowUsage} parent features. Adding a required input field may break the run subflow action if they don't send the required input.`,
				yesText: 'Make input required',
				noText: 'Cancel',
			};
			return this.guardFunction(dialogProps);
		},
		async deleteGuardFunc() {
			const dialogProps = {
				title: `Are you sure you want to delete this ${this.fieldTypeText} field?`,
				description: `This subflow is used by ${this.amountSubflowUsage} parent features. Deleting an ${this.fieldTypeText} field will remove any mappings into the field in parent features.`,
				yesText: 'Delete field',
				noText: 'Cancel',
			};
			return this.guardFunction(dialogProps);
		},
		async guardFunction(props) {
			if (this.subflowsUsageWorkflowIds.length === 0) return true;
			const res = await AppDialogManagerService.openConfirmationDialog({ ...props, isDanger: false });
			return res === APP_CONFIRMATION_DIALOG_RESPONSES.YES;
		},
	},
};
</script>

<style scoped lang="scss">
.field-definition {
	padding: 10px 8px;
	border: 1px solid var(--seconday-border-color);
	background-color: var(--secondary-bg-color);
	border-radius: 7px;

	.field-required .swtch {
		margin-top: 4px !important;
	}
	.inner-prepended-data-type {
		transform: translate(-2px, -6px);
	}
}
</style>
